import { defineStore } from "pinia";
import { computed, ref } from "vue";
import {
  getProofreadingRules,
  ProofreadingCategoryEntry,
  ProofreadingRuleEntry,
} from "@/lib/api/proofreading";
import { getDefaultSelectedRuleIds } from "@/lib/utils/proofreadingRuleCategories";

const STORAGE_KEY = "proofreadingBasic";

type PersistedState = {
  rules: ProofreadingRuleEntry[];
  categories: ProofreadingCategoryEntry[];
  selectedRuleIds: string[];
  selectedManuscriptKeys: string[];
  /** 固有名詞除外マスタトグル（既定 true = backend 既定に合わせる） */
  excludeProperNounsEnabled?: boolean;
  /**
   * 作品ごとの除外対象 properNounId。
   * キー未設定 / null = 登録済みすべて。配列 = 個別選択。
   */
  excludedProperNounIdsByNovelId?: Record<string, string[] | null>;
};

function loadPersistedState(): PersistedState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedState;
  } catch {
    return null;
  }
}

function savePersistedState(state: PersistedState): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage が使えない環境では永続化をスキップする
  }
}

/**
 * 基本校正ウィザード用 Pinia ストア。
 *
 * ウィザードをまたぐ選択状態とルールマスタのみ保持する。
 * 校正実行結果・エラー・ローディングは各コンポーネントのローカル状態で管理する。
 */
export const useProofreadingBasicStore = defineStore("proofreadingBasic", () => {
  const persisted = loadPersistedState();

  const rules = ref<ProofreadingRuleEntry[]>(persisted?.rules ?? []);
  const categories = ref<ProofreadingCategoryEntry[]>(persisted?.categories ?? []);
  const selectedRuleIds = ref<string[]>(persisted?.selectedRuleIds ?? []);
  const selectedManuscriptKeys = ref<string[]>(persisted?.selectedManuscriptKeys ?? []);
  const excludeProperNounsEnabled = ref<boolean>(
    persisted?.excludeProperNounsEnabled ?? true,
  );
  const activeNovelId = ref<string | null>(null);
  const excludedProperNounIdsByNovelId = ref<Record<string, string[] | null>>(
    persisted?.excludedProperNounIdsByNovelId ?? {},
  );

  /** 現在の作品に紐づく除外 ID。未 bind / 未設定は null（= すべて） */
  const excludedProperNounIds = computed((): string[] | null => {
    const novelId = activeNovelId.value;
    if (!novelId) return null;
    const value = excludedProperNounIdsByNovelId.value[novelId];
    return value === undefined ? null : value;
  });

  const selectedRuleIdSet = computed(() => new Set(selectedRuleIds.value));

  function persist(): void {
    savePersistedState({
      rules: rules.value,
      categories: categories.value,
      selectedRuleIds: selectedRuleIds.value,
      selectedManuscriptKeys: selectedManuscriptKeys.value,
      excludeProperNounsEnabled: excludeProperNounsEnabled.value,
      excludedProperNounIdsByNovelId: excludedProperNounIdsByNovelId.value,
    });
  }

  function setRules(payload: {
    rules: ProofreadingRuleEntry[];
    categories: ProofreadingCategoryEntry[];
  }): void {
    rules.value = payload.rules;
    categories.value = payload.categories;
    persist();
  }

  function setSelectedRuleIds(ids: string[]): void {
    selectedRuleIds.value = [...ids];
    persist();
  }

  function setSelectedManuscriptKeys(keys: string[]): void {
    selectedManuscriptKeys.value = [...keys];
    persist();
  }

  function setExcludeProperNounsEnabled(enabled: boolean): void {
    excludeProperNounsEnabled.value = enabled;
    persist();
  }

  /** 作品スコープを切り替える（除外 ID は novelId 単位で保持） */
  function setActiveNovelId(novelId: string): void {
    if (!novelId || activeNovelId.value === novelId) return;
    activeNovelId.value = novelId;
  }

  function setExcludedProperNounIds(ids: string[] | null): void {
    const novelId = activeNovelId.value;
    if (!novelId) return;
    excludedProperNounIdsByNovelId.value = {
      ...excludedProperNounIdsByNovelId.value,
      [novelId]: ids == null ? null : [...ids],
    };
    persist();
  }

  async function fetchRules(): Promise<void> {
    const res = await getProofreadingRules();
    rules.value = res.rules;
    categories.value = res.categories;
    if (selectedRuleIds.value.length === 0) {
      selectedRuleIds.value = getDefaultSelectedRuleIds(res.rules);
    }
    persist();
  }

  return {
    rules,
    categories,
    selectedRuleIds,
    selectedManuscriptKeys,
    selectedRuleIdSet,
    excludeProperNounsEnabled,
    excludedProperNounIds,
    activeNovelId,
    setRules,
    setSelectedRuleIds,
    setSelectedManuscriptKeys,
    setExcludeProperNounsEnabled,
    setActiveNovelId,
    setExcludedProperNounIds,
    fetchRules,
  };
});

export type ProofreadingBasicStore = ReturnType<typeof useProofreadingBasicStore>;
